import { connect } from "react-redux";
import
{
    Box, Button, Dialog, DialogContent, DialogTitle, FormControl,
    InputLabel, MenuItem, Select, SelectChangeEvent, Tab, Tabs
} from "@mui/material";
import React, { ChangeEventHandler, useContext, useEffect, useState } from "react";
import { selectProject } from "../../../redux/selectors/editor";
import { selectConstants, selectProjects, selectUser } from "../../../redux/selectors/firebase";
import { PathwayUser, ProjectDetails } from "../../../redux/types/firebase";
import { FirebaseContext } from "../../firebase/FirebaseProvider";
import Constant from "./Constant";
import makeStyles from "@mui/styles/makeStyles";
import { humanCamelCase } from "../../../utility/humanReadable";
import { Constants } from "../../graph/StateTags";
import { TextFieldAuto } from "../../input/TextFieldAuto";
import { Form } from "../../input/Form";
import ConstantStyle, {intrinsics} from "./ConstantStyle";
import { constantHasConstraint } from "../../../utility/constantsTagsUtils";
import {ProjectLocks} from "./ProjectLocks";

const useStyles = makeStyles((theme) => ({
    content: {
        marginRight: theme.spacing(1),
        '&::-webkit-scrollbar': {
            width: '0.4em'
        },
        '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(0,0,0,.1)',
            borderRadius: '0.4em'
        }
    }
}));

interface TabPanelProps
{
    children?: React.ReactNode;
    id: string;
    value: string;
}

function TabPanel({ children, value, id }: TabPanelProps)
{
    return value === id ?
        <Box sx={{ margin: 2 }}>
            {children}
        </Box> : null
}

interface IProps
{
    constants: Constants
    onCancel: () => any
    project: string
    projects: { [key: string]: ProjectDetails }
    user: PathwayUser
}
function ProjectDialog({ constants, onCancel, project, projects, user }: IProps)
{
    const classes = useStyles();
    const projectDetails = projects[project];
    const admin = user.roles.admin !== undefined;
    const [cloneProject, setCloneProject] = useState("");
    const [constantExpanded, setConstantExpanded] = useState("");
    const [tab, setTab] = useState("sources");
    const [newConstant, setNewConstant] = useState("");
    const [cloneSource, setCloneSource] = useState<{ [project: string]: Constants }>({});
    const { get, set, setProjectMember } = useContext(FirebaseContext);
    const handleCommit = (member: string, value: unknown) =>
    {
        setProjectMember(project, member, value).then();
    };
    const handleAddConstantClick = () =>
    {
        set(`constants/${project}/${newConstant}`, {
            name: humanCamelCase(newConstant),
            type: "string"
        }).then(() => setNewConstant(""));
    }
    const handleCloneClick = () =>
    {
        if (cloneSource[cloneProject])
        {
            Promise.all(Object.keys(cloneSource[cloneProject]).map(constant =>
                set(`constants/${project}/${constant}`, cloneSource[cloneProject][constant]))).then(() =>
                    console.log(`Cloned ${cloneProject}`));
        }
    };
    const handleCloneProjectChange = ({ target }: SelectChangeEvent) => setCloneProject(target.value);
    const handleConstantChange: ChangeEventHandler<HTMLInputElement> = ({ target }) =>
        setNewConstant(target.value);

    useEffect(() =>
    {
        get(`constants`).then(constants =>
        {
            const source = constants as { [project: string]: Constants } || {};

            delete source[project];
            setCloneSource(source);
            setCloneProject(Object.keys(source).shift() || "")
        });
    }, [get, project, setCloneSource])
    return (
        <Dialog fullWidth open={true} onClose={onCancel}>
            <DialogTitle>Project Details</DialogTitle>
            <DialogContent className={classes.content}>
                <Form display="flex" flexDirection="column">
                    <TextFieldAuto label="Name" value={projectDetails.name as string}
                        onCommit={(value) => handleCommit("name", value)} sx={{ m: 1 }} />
                    <TextFieldAuto label="Description" value={projectDetails.description as string}
                        rows={4} onCommit={(value) => handleCommit("description", value)} sx={{ m: 1 }} />
                </Form>

                <Box sx={{ width: '100%' }}>
                    <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                        <Tabs value={tab} onChange={(e, tab) => setTab(tab)} scrollButtons="auto"
                              variant="scrollable">
                            <Tab label="Project Sources" value={"sources"} />
                            <Tab label="Project Constants" value={"constants"} />
                            <Tab label="Constant Styles" value={"styles"} />
                            <Tab label="Project Locks" value={"locks"} />
                        </Tabs>
                    </Box>
                </Box>

                <TabPanel id={"sources"} value={tab}>
                    <Form display="flex" flexDirection="column">
                        <TextFieldAuto
                            label="Audio Source" value={projectDetails.audioSource as string}
                            onCommit={(value) => handleCommit("audioSource", value)} sx={{ m: 1 }} />
                        <TextFieldAuto
                            label="Image Source" value={projectDetails.imageSource as string}
                            onCommit={(value) => handleCommit("imageSource", value)} sx={{ m: 1 }} />
                        <TextFieldAuto
                            label="Video Source" value={projectDetails.videoSource as string}
                            onCommit={(value) => handleCommit("videoSource", value)} sx={{ m: 1 }} />
                        <TextFieldAuto
                            label="Style Source" value={projectDetails.styleSource as string}
                            onCommit={(value) => handleCommit("styleSource", value)} sx={{ m: 1 }} />
                        <TextFieldAuto
                            label="Script Sequence Tag" value={projectDetails.scriptSequenceTag ?? ""}
                            select
                            SelectProps={{
                                value: projectDetails.scriptSequenceTag ?? "",
                                onChange: (e) =>
                                {
                                    handleCommit("scriptSequenceTag", e.target.value);
                                }
                            }} sx={{ m: 1 }} >
                                {Object.keys(constants || {})
                                .filter((id)=>constantHasConstraint(constants[id], "orderable"))
                                .map((id)=><MenuItem key={id} value={id}>{constants[id].name}</MenuItem>)}
                            </TextFieldAuto>
                    </Form>
                </TabPanel>
                <TabPanel id={"constants"} value={tab}>
                    <Box m={1} display="flex">
                        <FormControl fullWidth>
                            <InputLabel>Project</InputLabel>
                            <Select label="Project" value={cloneProject} onChange={handleCloneProjectChange} size="small">
                                {Object.keys(cloneSource || {}).map(projectKey =>
                                    <MenuItem key={projectKey} value={projectKey}>
                                        {projects[projectKey].name}
                                    </MenuItem>
                                )}
                            </Select>
                        </FormControl>
                        <Button disabled={!admin} onClick={handleCloneClick} variant="contained"
                            sx={{ marginLeft: 1 }}>Clone</Button>
                    </Box>
                    <Form display="flex" onCommit={handleAddConstantClick}>
                        <TextFieldAuto label="New constant name" value={newConstant} size="small"
                            fullWidth sx={{ m: 1 }} onChange={handleConstantChange} />
                        <Button disabled={!admin || newConstant === "" || (constants !== null && constants[newConstant] !== undefined)}
                            onClick={handleAddConstantClick} variant="contained" sx={{ m: 1 }} type="button">
                            Add
                        </Button>
                    </Form>
                    {Object.keys(constants || {})
                        .filter(constant => ! intrinsics[constant])
                        .map(constant => (
                            <Constant key={constant} admin={admin} constant={constants[constant]}
                                expanded={constantExpanded} name={constant} project={project}
                                setExpanded={setConstantExpanded} />
                    ))}
                </TabPanel>
                <TabPanel id="locks" value={tab}>
                    <ProjectLocks project={project}/>
                </TabPanel>
                <TabPanel id={"styles"} value={tab}>
                    <ConstantStyle />
                </TabPanel>
            </DialogContent>
        </Dialog>
    );
}
const mapStateToProps = (store: never) => ({
    constants: selectConstants(store),
    project: selectProject(store),
    projects: selectProjects(store),
    user: selectUser(store),
});

export default connect(mapStateToProps, {
})(ProjectDialog);
